import { useEffect, useState } from 'react';
import { Button, Input } from 'antd';
import { LinkOutlined, SearchOutlined } from '@ant-design/icons';
import { Permission } from '@aid/shared';
import type { KeycloakUserDto } from '../../api/types';
import { AdminListToolbar } from '../../components/AdminListToolbar';
import { AdminBreadcrumbs } from '../../components/AdminBreadcrumbs';
import { DataTable } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatusTag } from '../../components/StatusTag';
import { useHasPermission } from '../../auth/useHasPermission';
import { useT } from '../../locales';
import { paginationProps, usePaginacion } from '../../lib/paginacion';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { LinkKeycloakUserModal } from './components/LinkKeycloakUserModal';
import { useKeycloakOrphans } from './api/useKeycloakOrphans';

export function KeycloakOrphansPage() {
  const t = useT();
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<KeycloakUserDto | null>(null);
  const debouncedSearch = useDebouncedValue(search.trim());
  const { page, pageSize, handleChange, setPage } = usePaginacion(20);
  const canLink = useHasPermission(Permission.USERS_CREATE);

  const { data, isLoading, isFetching, error, refetch } = useKeycloakOrphans({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: debouncedSearch || undefined,
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, setPage]);

  const clearFilters = () => {
    setSearch('');
    setPage(1);
  };

  const hasFilters = Boolean(debouncedSearch);
  const emptyState = (
    <EmptyState
      title={t(
        hasFilters
          ? 'pages.keycloakOrphans.emptyFilteredTitle'
          : 'pages.keycloakOrphans.emptyTitle',
      )}
      description={t(
        hasFilters
          ? 'pages.keycloakOrphans.emptyFilteredDescription'
          : 'pages.keycloakOrphans.emptyDescription',
      )}
      action={hasFilters ? <Button onClick={clearFilters}>{t('common.clear')}</Button> : undefined}
    />
  );

  const columns = [
    {
      title: t('pages.keycloakOrphans.keycloakId'),
      dataIndex: 'id' as keyof KeycloakUserDto,
      key: 'id',
      ellipsis: true,
    },
    {
      title: t('pages.keycloakOrphans.username'),
      dataIndex: 'username' as keyof KeycloakUserDto,
      key: 'username',
    },
    {
      title: t('pages.keycloakOrphans.email'),
      dataIndex: 'email' as keyof KeycloakUserDto,
      key: 'email',
    },
    {
      title: t('pages.keycloakOrphans.firstName'),
      dataIndex: 'firstName' as keyof KeycloakUserDto,
      key: 'firstName',
      render: (value: string) => value || '-',
    },
    {
      title: t('pages.keycloakOrphans.lastName'),
      dataIndex: 'lastName' as keyof KeycloakUserDto,
      key: 'lastName',
      render: (value: string) => value || '-',
    },
    {
      title: t('pages.keycloakOrphans.status'),
      dataIndex: 'enabled' as keyof KeycloakUserDto,
      key: 'enabled',
      render: (enabled: boolean) => (
        <StatusTag tone={enabled ? 'success' : 'error'}>
          {enabled ? t('pages.keycloakOrphans.enabled') : t('pages.keycloakOrphans.disabled')}
        </StatusTag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_value: unknown, record: KeycloakUserDto) =>
        canLink ? (
          <Button
            type="text"
            icon={<LinkOutlined />}
            aria-label={t('pages.keycloakOrphans.linkAction')}
            onClick={() => setSelectedUser(record)}
          >
            {t('pages.keycloakOrphans.linkAction')}
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        breadcrumb={<AdminBreadcrumbs current={t('pages.keycloakOrphans.title')} />}
        title={t('pages.keycloakOrphans.title')}
        subtitle={t('pages.keycloakOrphans.subtitle')}
      />
      <AdminListToolbar
        filters={
          <Input
            aria-label={t('pages.keycloakOrphans.searchPlaceholder')}
            placeholder={t('pages.keycloakOrphans.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            allowClear
            style={{ width: 300, maxWidth: '100%' }}
          />
        }
      />
      {error ? (
        <EmptyState
          title={t('pages.keycloakOrphans.errorLoad')}
          description={t('common.error')}
          action={<Button onClick={() => void refetch()}>{t('common.retry')}</Button>}
        />
      ) : (
        <DataTable<KeycloakUserDto>
          columns={columns}
          dataSource={data?.data ?? []}
          rowKey="id"
          loading={isLoading || isFetching}
          pagination={paginationProps(t, { page, pageSize, total: data?.total ?? 0 })}
          onChange={handleChange}
          locale={{ emptyText: emptyState }}
        />
      )}
      {selectedUser && (
        <LinkKeycloakUserModal open user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
    </div>
  );
}
